import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Eye, Database, Users, MessageCircle, FileText, AlertTriangle, CheckCircle, ArrowLeft } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import Card from '../../components/ui/Card.jsx';

function PrivacyPolicyPage({ onBack }) {
  const [activeSection, setActiveSection] = useState('overview');
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      // If no onBack prop provided, navigate to login page
      navigate('/login');
    }
  };

  const sections = [
    { id: 'overview', title: 'Overview', icon: Shield },
    { id: 'data-collection', title: 'Data Collection', icon: Database },
    { id: 'whatsapp-integration', title: 'WhatsApp Integration', icon: MessageCircle },
    { id: 'security-measures', title: 'Security Measures', icon: Lock },
    { id: 'data-retention', title: 'Data Retention', icon: FileText },
    { id: 'user-rights', title: 'User Rights', icon: Users },
    { id: 'compliance', title: 'Compliance', icon: CheckCircle },
    { id: 'contact', title: 'Contact', icon: AlertTriangle }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-brand-primary rounded-lg flex items-center justify-center mb-4">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Privacy Policy
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                SRPF Command Dashboard - Last Updated: {new Date().toLocaleDateString()}
              </p>
            </div>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Introduction
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  The SRPF Command Dashboard ("we," "our," or "us") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform, including our WhatsApp integration services.
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                  By using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
                </p>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Key Principles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Transparency</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Clear communication about data practices</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Minimization</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Collect only necessary data</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Security</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Robust protection of your information</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Control</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">You control your data</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'data-collection':
        return (
          <div className="space-y-6">
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Information We Collect
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Personal Information</h4>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1">
                      <li>Full name and employee identification details</li>
                      <li>Email addresses and phone numbers</li>
                      <li>Department and designation information</li>
                      <li>User authentication credentials</li>
                      <li>Profile pictures and identification documents</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">WhatsApp Integration Data</h4>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1">
                      <li>WhatsApp phone numbers and contact information</li>
                      <li>Message content and metadata</li>
                      <li>Message delivery status and timestamps</li>
                      <li>Conversation history and chat logs</li>
                      <li>Broadcast campaign data and recipient lists</li>
                      <li>Grievance ticket information and responses</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">System Usage Data</h4>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1">
                      <li>Login timestamps and session information</li>
                      <li>Feature usage patterns and preferences</li>
                      <li>Device information and browser details</li>
                      <li>IP addresses and location data</li>
                      <li>Error logs and performance metrics</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  How We Collect Information
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-brand-primary rounded-full mt-2"></div>
                    <p className="text-gray-600 dark:text-gray-400">Directly from you when you register, update your profile, or use our services</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-brand-primary rounded-full mt-2"></div>
                    <p className="text-gray-600 dark:text-gray-400">Through WhatsApp integration when you interact with our bot or receive messages</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-brand-primary rounded-full mt-2"></div>
                    <p className="text-gray-600 dark:text-gray-400">Automatically through system logs, cookies, and analytics tools</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-brand-primary rounded-full mt-2"></div>
                    <p className="text-gray-600 dark:text-gray-400">From third-party services integrated with our platform</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'whatsapp-integration':
        return (
          <div className="space-y-6">
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  WhatsApp Integration Privacy
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Our WhatsApp integration is designed to enhance communication while maintaining the highest privacy standards. Here's how we handle your WhatsApp data:
                </p>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  WhatsApp Data Processing
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Message Content</h4>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1">
                      <li>Messages are encrypted in transit using WhatsApp's end-to-end encryption</li>
                      <li>Message content is stored securely on our servers for operational purposes</li>
                      <li>We do not share message content with third parties without your consent</li>
                      <li>Message metadata (timestamps, delivery status) is used for system functionality</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Contact Information</h4>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1">
                      <li>Phone numbers are used solely for WhatsApp communication</li>
                      <li>Contact names and details are stored for identification purposes</li>
                      <li>We do not sell or share contact information with external parties</li>
                      <li>Contact data is used only for legitimate business purposes</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Broadcast Campaigns</h4>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1">
                      <li>Recipient lists are maintained securely and confidentially</li>
                      <li>Campaign content is reviewed for compliance before sending</li>
                      <li>Opt-out mechanisms are provided for all broadcast messages</li>
                      <li>Campaign performance data is used for system improvement only</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  WhatsApp Security Measures
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <Lock className="h-5 w-5 text-green-600 mt-1" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">End-to-End Encryption</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">All messages are encrypted using WhatsApp's security protocols</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Lock className="h-5 w-5 text-green-600 mt-1" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">Secure Storage</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Data is stored in encrypted databases with access controls</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <Lock className="h-5 w-5 text-green-600 mt-1" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">Access Controls</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Role-based access ensures only authorized personnel can view data</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Lock className="h-5 w-5 text-green-600 mt-1" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">Audit Logging</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">All WhatsApp interactions are logged for security monitoring</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'security-measures':
        return (
          <div className="space-y-6">
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Data Security
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  We implement comprehensive security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction.
                </p>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Technical Safeguards
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Encryption</h4>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1">
                      <li>AES-256 encryption for data at rest</li>
                      <li>TLS 1.3 encryption for data in transit</li>
                      <li>End-to-end encryption for WhatsApp messages</li>
                      <li>Encrypted backups and database storage</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Access Controls</h4>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1">
                      <li>Multi-factor authentication for all user accounts</li>
                      <li>Role-based access control (RBAC) system</li>
                      <li>Regular access reviews and permission audits</li>
                      <li>Principle of least privilege access</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Network Security</h4>
                    <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1">
                      <li>Firewall protection and intrusion detection</li>
                      <li>DDoS protection and traffic monitoring</li>
                      <li>Secure VPN access for administrative functions</li>
                      <li>Regular security vulnerability assessments</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Administrative Safeguards
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <Shield className="h-5 w-5 text-blue-600 mt-1" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">Staff Training</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Regular privacy and security training for all staff members</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Shield className="h-5 w-5 text-blue-600 mt-1" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">Incident Response</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Comprehensive incident response plan for security breaches</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <Shield className="h-5 w-5 text-blue-600 mt-1" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">Regular Audits</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Quarterly security audits and compliance reviews</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Shield className="h-5 w-5 text-blue-600 mt-1" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">Data Minimization</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Collect and retain only necessary data</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'data-retention':
        return (
          <div className="space-y-6">
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Data Retention Policy
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  We retain your personal information only for as long as necessary to fulfill the purposes outlined in this privacy policy, unless a longer retention period is required or permitted by law.
                </p>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Retention Periods
                </h3>
                <div className="space-y-4">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-medium text-gray-900 dark:text-white">User Account Data</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Retained for the duration of employment plus 7 years for legal compliance</p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-medium text-gray-900 dark:text-white">WhatsApp Messages</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Retained for 3 years for operational purposes, then securely deleted</p>
                  </div>
                  <div className="border-l-4 border-yellow-500 pl-4">
                    <h4 className="font-medium text-gray-900 dark:text-white">System Logs</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Retained for 1 year for security monitoring and troubleshooting</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-medium text-gray-900 dark:text-white">Audit Records</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Retained for 7 years for compliance and legal requirements</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Data Deletion
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                    <p className="text-gray-600 dark:text-gray-400">Personal data is securely deleted using industry-standard methods</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                    <p className="text-gray-600 dark:text-gray-400">Backup data is also deleted within the retention period</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                    <p className="text-gray-600 dark:text-gray-400">WhatsApp message data is permanently removed from all systems</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                    <p className="text-gray-600 dark:text-gray-400">Certificates of deletion are provided upon request</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'user-rights':
        return (
          <div className="space-y-6">
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Your Rights
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  You have certain rights regarding your personal information. We are committed to helping you exercise these rights.
                </p>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Data Subject Rights
                </h3>
                <div className="space-y-4">
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Right to Access</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">You can request a copy of all personal data we hold about you, including WhatsApp message history.</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Right to Rectification</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">You can request correction of inaccurate or incomplete personal data.</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Right to Erasure</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">You can request deletion of your personal data, subject to legal obligations.</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Right to Portability</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">You can request a copy of your data in a machine-readable format.</p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Right to Object</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">You can object to processing of your data for certain purposes.</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  WhatsApp-Specific Rights
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <MessageCircle className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Message History Access</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Request a copy of your WhatsApp conversation history</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <MessageCircle className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Opt-Out from Broadcasts</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Unsubscribe from broadcast messages at any time</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <MessageCircle className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Contact Information Update</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Update your WhatsApp contact information</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'compliance':
        return (
          <div className="space-y-6">
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Legal Compliance
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  We comply with applicable data protection laws and regulations to ensure your privacy rights are protected.
                </p>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Applicable Laws
                </h3>
                <div className="space-y-4">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h4 className="font-medium text-gray-900 dark:text-white">Data Protection Act 2018</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">UK data protection legislation compliance</p>
                  </div>
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-medium text-gray-900 dark:text-white">GDPR (General Data Protection Regulation)</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">EU data protection and privacy regulation</p>
                  </div>
                  <div className="border-l-4 border-yellow-500 pl-4">
                    <h4 className="font-medium text-gray-900 dark:text-white">WhatsApp Business API Terms</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Compliance with WhatsApp's business API requirements</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="font-medium text-gray-900 dark:text-white">Local Employment Laws</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Adherence to relevant employment and workplace regulations</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Compliance Measures
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-1" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">Privacy Impact Assessments</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Regular assessments of data processing activities</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-1" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">Data Protection Officer</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Designated DPO for privacy compliance oversight</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-1" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">Regular Audits</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Quarterly compliance audits and reviews</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-1" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">Staff Training</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Ongoing privacy and compliance training</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-6">
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Contact Information
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  If you have any questions about this Privacy Policy or our data practices, please contact us using the information below.
                </p>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Data Protection Officer
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Email</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">dpo@srpf-command.gov.uk</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Phone</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">+44 (0) 20 7944 0000</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Address</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        SRPF Command Dashboard<br />
                        Data Protection Office<br />
                        London, United Kingdom
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Response Times
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">General Inquiries</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">We will respond within 5 business days</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Data Subject Requests</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">We will respond within 30 days as required by law</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-orange-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Security Incidents</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Immediate response for security-related concerns</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Policy Updates
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                  We encourage you to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
                </p>
              </div>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Login</span>
            </Button>
            <div className="text-right">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Privacy Policy
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                SRPF Command Dashboard
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8">
              <div className="p-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  Table of Contents
                </h3>
                <nav className="space-y-2">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.id}
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full flex items-center space-x-3 px-3 py-2 text-left rounded-lg transition-colors ${
                          activeSection === section.id
                            ? 'bg-brand-primary text-white'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="text-sm font-medium">{section.title}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicyPage;
